import { useMemberProfile } from "./MemberProfileContext";
import { ProfileOverlays } from "./overlays/ProfileOverlays";
import { CategoryBlock } from "./profilePrimitives";
import { travelMissing, travelRows } from "./profileRows";
import { listMissingLabels } from "./profileUtils";
import { AdminSection } from "./sections/AdminSection";
import { ArchivedBanner, ArchiveSection } from "./sections/ArchiveSection";
import { ContactSection } from "./sections/ContactSection";
import { DocumentsSection } from "./sections/DocumentsSection";
import { FamilySection } from "./sections/FamilySection";
import { MembershipSection } from "./sections/MembershipSection";
import { OnboardingSection } from "./sections/OnboardingSection";
import { PaymentsSection } from "./sections/PaymentsSection";
import { PersonalSection } from "./sections/PersonalSection";
import { ReferralsSection } from "./sections/ReferralsSection";
import { TestMemberBanner } from "./sections/TestMemberBanner";
import { VipMemberBanner } from "./sections/VipMemberBanner";

export function MemberProfile() {
    const { profile, membershipTiers, isAdmin, setOverlay, openProactivityMessage } =
        useMemberProfile();

    return (
        <div className="relative flex h-full w-full flex-col overflow-hidden bg-white">
            {/* The banners stay outside the scroll area so that they remain
                visible while agents scroll through the profile. */}
            {profile.isVipMember && <VipMemberBanner />}
            {profile.isTestMember && <TestMemberBanner />}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {profile.archived && (
                    <ArchivedBanner
                        archived={profile.archived}
                        canActivate={isAdmin}
                        onActivate={() => setOverlay({ type: "activate" })}
                    />
                )}
                <MembershipSection
                    memberId={profile.memberId}
                    membership={profile.membership}
                    membershipTiers={membershipTiers}
                    canEdit={isAdmin}
                    setOverlay={setOverlay}
                    onOpenProactivity={openProactivityMessage}
                />
                <PersonalSection personal={profile.personal} setOverlay={setOverlay} />
                <FamilySection
                    familyMustKnow={profile.familyMustKnow}
                    family={profile.family}
                    setOverlay={setOverlay}
                />
                <PaymentsSection payments={profile.payments} setOverlay={setOverlay} />
                <ContactSection contact={profile.contact} setOverlay={setOverlay} />
                <AdminSection
                    memberId={profile.memberId}
                    admin={profile.admin}
                    setOverlay={setOverlay}
                />
                <DocumentsSection
                    documents={profile.documents}
                    setOverlay={setOverlay}
                />

                <CategoryBlock
                    title="Dining"
                    notes={profile.dining.diningPreferences}
                    rows={
                        profile.dining.foodAllergies
                            ? [
                                  {
                                      label: "Restrictions",
                                      value: profile.dining.foodAllergies,
                                  },
                              ]
                            : undefined
                    }
                    futurePlan={profile.dining.futurePlan}
                    onboardingFuturePlan={profile.dining.onboardingFuturePlan}
                    missing={listMissingLabels([
                        {
                            label: "Must know",
                            present: !!profile.dining.diningPreferences,
                        },
                        {
                            label: "Restrictions",
                            present: !!profile.dining.foodAllergies,
                        },
                        {
                            label: "Future plans",
                            present: !!(
                                profile.dining.futurePlan ||
                                profile.dining.onboardingFuturePlan
                            ),
                        },
                    ])}
                    onEdit={() => setOverlay({ type: "edit", section: "dining" })}
                />
                <CategoryBlock
                    title="Travel"
                    notes={profile.travel.travelMustKnow}
                    rows={travelRows(profile.travel)}
                    futurePlan={profile.travel.futurePlan}
                    onboardingFuturePlan={profile.travel.onboardingFuturePlan}
                    missing={travelMissing(profile.travel)}
                    onEdit={() => setOverlay({ type: "edit", section: "travel" })}
                />
                <CategoryBlock
                    title="Events"
                    notes={profile.events.eventsAndExperiences}
                    missing={listMissingLabels([
                        {
                            label: "Must know",
                            present: !!profile.events.eventsAndExperiences,
                        },
                    ])}
                    onEdit={() => setOverlay({ type: "edit", section: "events" })}
                />
                <CategoryBlock
                    title="Wellness"
                    notes={profile.wellness.wellnessMustKnow}
                    missing={listMissingLabels([
                        {
                            label: "Must know",
                            present: !!profile.wellness.wellnessMustKnow,
                        },
                    ])}
                    onEdit={() => setOverlay({ type: "edit", section: "wellness" })}
                />
                <CategoryBlock
                    title="Shopping"
                    notes={profile.shopping.luxuryItemsAndCollectibles}
                    missing={listMissingLabels([
                        {
                            label: "Must know",
                            present: !!profile.shopping.luxuryItemsAndCollectibles,
                        },
                    ])}
                    onEdit={() => setOverlay({ type: "edit", section: "shopping" })}
                />

                <OnboardingSection
                    onboarding={profile.onboarding}
                    setOverlay={setOverlay}
                />
                <ReferralsSection
                    referrals={profile.referrals}
                    setOverlay={setOverlay}
                />
                {!profile.archived && isAdmin && (
                    <ArchiveSection
                        groups={profile.membership.groups}
                        setOverlay={setOverlay}
                    />
                )}
            </div>

            <ProfileOverlays />
        </div>
    );
}
